import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { useNavigate, useSearchParams } from "react-router-dom";
import safeRedirect from "../safeRedirect";
import { useTranslation } from "react-i18next";
import { AUTH_API_URL as API_AUTH_URL } from "../config";
import { Capacitor } from '@capacitor/core';
import { rememberMobileLogin } from '../auth/mobileSession';

const DEFAULT_METHOD = {
  login: "legacy",
  signup: "whatsapp",
};

function decodeAndStoreToken(token) {
  localStorage.setItem("token", token);
  try {
    const payload = JSON.parse(atob(token.split(".")[1]));
    const userId = payload.userId || payload.user_id || payload.sub;
    if (userId) {
      localStorage.setItem("userId", userId);
    }
  } catch (_error) {
    // Ignore malformed payloads here, auth success is handled by token storage.
  }
}

function formatCountdown(seconds) {
  const safeSeconds = Math.max(0, seconds);
  const minutes = Math.floor(safeSeconds / 60);
  const remainingSeconds = safeSeconds % 60;
  return `${minutes}:${remainingSeconds.toString().padStart(2, "0")}`;
}

function normalizePhone(phone) {
  if (typeof phone !== "string") {
    return "";
  }

  let normalized = phone.trim().replace(/[\s().-]/g, "");
  if (normalized.startsWith("00")) {
    normalized = `+${normalized.slice(2)}`;
  }
  return normalized;
}

const AuthPage = ({ onAuthSuccess }) => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const isAndroidApp = Capacitor.getPlatform() === "android";
  // Arriving from a project invitation (/join/<token>): open on sign-up with
  // the invited address filled in, and come back to the invitation afterwards.
  const [searchParams] = useSearchParams();
  const initialMode = searchParams.get("mode") === "signup" ? "signup" : "login";
  const redirectTo = safeRedirect(searchParams.get("redirect"));

  const [mode, setMode] = useState(initialMode);
  const [authMethod, setAuthMethod] = useState(DEFAULT_METHOD[initialMode]);
  const [step, setStep] = useState("start");
  const [email, setEmail] = useState(searchParams.get("email") || "");
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [otp, setOtp] = useState("");
  const [challenge, setChallenge] = useState(null);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [secondsRemaining, setSecondsRemaining] = useState(0);

  const resetFlow = () => {
    setStep("start");
    setChallenge(null);
    setOtp("");
    setMessage("");
    setError("");
    setLoading(false);
    setSecondsRemaining(0);
  };

  const setModeWithDefaults = (nextMode) => {
    setMode(nextMode);
    setAuthMethod(DEFAULT_METHOD[nextMode]);
    setPassword("");
    setConfirmPassword("");
    resetFlow();
  };

  const handleMethodChange = (nextMethod) => {
    setAuthMethod(nextMethod);
    setPassword("");
    setConfirmPassword("");
    resetFlow();
  };

  const completeAuth = useCallback(
    async (token, refreshToken) => {
      let mobileRefreshToken = refreshToken;
      if (Capacitor.getPlatform() === 'android' && !mobileRefreshToken) {
        const response = await axios.post(`${API_AUTH_URL}/mobile-session/issue`, {}, {
          headers: { Authorization: `Bearer ${token}` }, timeout: 10000,
        });
        mobileRefreshToken = response.data?.refreshToken;
        if (!mobileRefreshToken) throw new Error('Mobile session could not be created.');
      }
      await rememberMobileLogin({ token, refreshToken: mobileRefreshToken });
      decodeAndStoreToken(token);
      onAuthSuccess(token);
      navigate(redirectTo || "/chat");
    },
    [navigate, onAuthSuccess, redirectTo]
  );

  useEffect(() => {
    if (!challenge?.expiresAt) {
      setSecondsRemaining(0);
      return undefined;
    }

    const updateCountdown = () => {
      const expiresAt = new Date(challenge.expiresAt).getTime();
      const remaining = Math.ceil((expiresAt - Date.now()) / 1000);
      setSecondsRemaining(Math.max(0, remaining));
    };

    updateCountdown();
    const intervalId = window.setInterval(updateCountdown, 1000);
    return () => window.clearInterval(intervalId);
  }, [challenge?.expiresAt]);

  useEffect(() => {
    if (
      step !== "wait_whatsapp" ||
      !challenge?.challengeId ||
      challenge?.status !== "pending"
    ) {
      return undefined;
    }

    let cancelled = false;

    const pollStatus = async () => {
      try {
        const response = await axios.get(
          `${API_AUTH_URL}/auth/challenges/${challenge.challengeId}/status`
        );

        if (cancelled) {
          return;
        }

        const nextStatus = response.data?.status;
        if (nextStatus === "verified" && response.data?.token) {
          await completeAuth(response.data.token, response.data.refreshToken);
          return;
        }

        if (nextStatus === "expired") {
          setChallenge((previous) =>
            previous ? { ...previous, status: "expired" } : previous
          );
          setError(t("auth.challenge_expired", "This code has expired. Start again."));
          setMessage("");
          return;
        }

        if (nextStatus === "failed") {
          setChallenge((previous) =>
            previous ? { ...previous, status: "failed" } : previous
          );
          setError(
            t("auth.challenge_failed", "This verification request is no longer valid.")
          );
        }
      } catch (_error) {
        if (!cancelled) {
          setError(t("auth.error", "Authentication error."));
        }
      }
    };

    pollStatus();
    const intervalId = window.setInterval(pollStatus, 2000);
    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
    };
  }, [challenge?.challengeId, challenge?.status, completeAuth, step, t]);

  const validatePhone = () => /^\+[1-9]\d{7,14}$/.test(normalizePhone(phone));

  const validatePasswordPair = () => {
    if (password.length < 6) {
      setError(
        t("auth.password_too_short", "Password must contain at least 6 characters.")
      );
      return false;
    }

    if (password !== confirmPassword) {
      setError(t("auth.password_no_match", "Passwords do not match."));
      return false;
    }

    return true;
  };

  const handleLegacyLogin = async (event) => {
    event.preventDefault();
    setLoading(true);
    setError("");
    setMessage("");

    try {
      const response = await axios.post(
        `${API_AUTH_URL}/login`,
        { email, password, ...(Capacitor.getPlatform() === 'android' ? { platform: 'android' } : {}) },
        { headers: { "Content-Type": "application/json" } }
      );
      await completeAuth(response.data.token, response.data.refreshToken);
    } catch (requestError) {
      setError(requestError.response?.data?.error || t("auth.error", "Authentication error."));
    } finally {
      setLoading(false);
    }
  };

  const handleEmailSignupStart = async (event) => {
    event.preventDefault();
    setLoading(true);
    setError("");
    setMessage("");

    if (!validatePasswordPair()) {
      setLoading(false);
      return;
    }

    try {
      const response = await axios.post(
        `${API_AUTH_URL}/auth/start`,
        { mode: "signup", channel: "email", email },
        { headers: { "Content-Type": "application/json" } }
      );

      setChallenge({ ...response.data, status: "pending" });
      setStep("verify_email");
      setMessage(
        t("auth.email_code_sent", "A verification code has been sent to your email.")
      );
    } catch (requestError) {
      setError(requestError.response?.data?.error || t("auth.error", "Authentication error."));
    } finally {
      setLoading(false);
    }
  };

  const handleEmailSignupVerify = async (event) => {
    event.preventDefault();
    if (!challenge?.challengeId) {
      return;
    }

    setLoading(true);
    setError("");

    try {
      const response = await axios.post(`${API_AUTH_URL}/auth/verify`, {
        challengeId: challenge.challengeId,
        otp,
        password,
      });
      await completeAuth(response.data.token, response.data.refreshToken);
    } catch (requestError) {
      setError(requestError.response?.data?.error || t("auth.invalid_code", "Invalid code."));
    } finally {
      setLoading(false);
    }
  };

  const startWhatsappChallenge = async (event) => {
    event.preventDefault();
    setLoading(true);
    setError("");
    setMessage("");

    if (!validatePhone()) {
      setError(
        t(
          "auth.invalid_phone",
          "Enter a valid WhatsApp number in international format, for example +212612345678."
        )
      );
      setLoading(false);
      return;
    }

    if (mode === "signup" && !validatePasswordPair()) {
      setLoading(false);
      return;
    }

    try {
      const payload = {
        mode,
        channel: "whatsapp",
        phone: normalizePhone(phone),
        language: i18n.language,
      };

      if (mode === "signup") {
        payload.password = password;
      }

      const response = await axios.post(`${API_AUTH_URL}/auth/start`, payload, {
        headers: { "Content-Type": "application/json" },
      });

      setPhone(response.data.phone || payload.phone);
      setChallenge({ ...response.data, status: "pending" });
      setStep("wait_whatsapp");
      setMessage(
        mode === "signup"
          ? t(
              "auth.whatsapp_signup_started",
              "Open WhatsApp and send the pre-filled message to create and link your account."
            )
          : t(
              "auth.whatsapp_login_started",
              "Open WhatsApp and send the pre-filled message to sign in."
            )
      );
    } catch (requestError) {
      setError(requestError.response?.data?.error || t("auth.error", "Authentication error."));
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (!challenge?.challengeId || challenge?.status !== "pending") {
      return;
    }

    setLoading(true);
    setError("");
    setMessage("");

    try {
      const response = await axios.post(`${API_AUTH_URL}/auth/resend`, {
        challengeId: challenge.challengeId,
      });
      setChallenge((previous) => ({
        ...(previous || {}),
        ...response.data,
        status: "pending",
      }));
      setMessage(
        challenge.channel === "whatsapp"
          ? t(
              "auth.whatsapp_code_resent",
              "A new WhatsApp code is ready. Open WhatsApp again and send the new message."
            )
          : t("auth.code_resent", "A new code has been sent.")
      );
    } catch (requestError) {
      setError(requestError.response?.data?.error || t("auth.error", "Authentication error."));
    } finally {
      setLoading(false);
    }
  };

  const openWhatsapp = () => {
    if (!challenge?.whatsappUrl) {
      return;
    }
    window.open(challenge.whatsappUrl, "_blank", "noopener,noreferrer");
  };

  const renderWhatsappWaiting = () => (
    <>
      <div className="auth-legacy-banner">
        <strong>
          {t("auth.whatsapp_waiting_title", "Finish in WhatsApp")}
        </strong>
        <span>
          {t(
            "auth.whatsapp_waiting_body",
            "Send the pre-filled message to the Upyachat bot. The app will sign you in automatically as soon as the message is received."
          )}
        </span>
      </div>

      <div className="auth-form auth-form-classic">
        <input
          type="text"
          value={challenge?.phone || phone}
          readOnly
          placeholder={t("auth.phone", "WhatsApp number")}
        />
        <input
          type="text"
          value={challenge?.code || ""}
          readOnly
          placeholder={t("auth.whatsapp_code", "WhatsApp code")}
        />
        <button type="button" onClick={openWhatsapp} disabled={!challenge?.whatsappUrl}>
          {t("whatsapp.mobile_link_btn", "Open WhatsApp")}
        </button>
        <button
          type="button"
          className="auth-secondary-button"
          onClick={handleResend}
          disabled={loading || challenge?.status !== "pending"}
        >
          {t("auth.resend_code", "Resend code")}
        </button>
      </div>

      <p className="success-message">
        {secondsRemaining > 0
          ? t("auth.whatsapp_countdown", {
              defaultValue: "Code expires in {{time}}.",
              time: formatCountdown(secondsRemaining),
            })
          : t("auth.challenge_expired", "This code has expired. Start again.")}
      </p>

      <button
        type="button"
        className="auth-text-link"
        onClick={resetFlow}
      >
        {t("auth.back", "Back")}
      </button>
    </>
  );

  const renderLogin = () => (
    <>
      <div className="auth-method-switch">
        <button
          type="button"
          className={`auth-method-pill ${authMethod === "whatsapp" ? "selected" : ""}`}
          onClick={() => handleMethodChange("whatsapp")}
        >
          {t("auth.method_whatsapp", "WhatsApp")}
        </button>
        <button
          type="button"
          className={`auth-method-pill ${authMethod === "legacy" ? "selected" : ""}`}
          onClick={() => handleMethodChange("legacy")}
        >
          {t("auth.method_legacy", "Password")}
        </button>
      </div>

      {authMethod === "legacy" ? (
        <>
          <form className="auth-form auth-form-classic" onSubmit={handleLegacyLogin}>
            <input
              type="email"
              placeholder={t("auth.email", "Email")}
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              required
            />
            <input
              type="password"
              placeholder={t("auth.password", "Password")}
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              required
            />
            <button type="submit" disabled={loading}>
              {loading ? t("auth.loading", "Loading...") : t("auth.login_btn", "Log In")}
            </button>
          </form>

          <button
            type="button"
            className="auth-text-link"
            onClick={() => navigate("/forgot-password")}
          >
            {t("auth.forgot_password", "Forgot your password?")}
          </button>
        </>
      ) : step === "wait_whatsapp" ? (
        renderWhatsappWaiting()
      ) : (
        <form className="auth-form auth-form-classic" onSubmit={startWhatsappChallenge}>
          <input
            type="tel"
            placeholder={t("auth.phone_placeholder", "Ex: +33612345678")}
            value={phone}
            onChange={(event) => setPhone(event.target.value)}
            required
          />
          <p className="auth-field-hint">
            {t(
              "auth.phone_helper",
              "Enter your number in international format, without the initial 0."
            )}
          </p>
          <button type="submit" disabled={loading}>
            {loading ? t("auth.loading", "Loading...") : t("auth.login_whatsapp_btn", "Sign in with WhatsApp")}
          </button>
        </form>
      )}

      {!isAndroidApp && (
        <button
          type="button"
          className="auth-switch-button"
          onClick={() => setModeWithDefaults("signup")}
        >
          {t("auth.signup_btn", "Sign Up")}
        </button>
      )}
    </>
  );

  const renderSignup = () => (
    <>
      <div className="auth-method-switch">
        <button
          type="button"
          className={`auth-method-pill ${authMethod === "whatsapp" ? "selected" : ""}`}
          onClick={() => handleMethodChange("whatsapp")}
        >
          {t("auth.method_whatsapp", "WhatsApp")}
        </button>
        <button
          type="button"
          className={`auth-method-pill ${authMethod === "email" ? "selected" : ""}`}
          onClick={() => handleMethodChange("email")}
        >
          {t("auth.method_email", "Email")}
        </button>
      </div>

      {authMethod === "email" ? (
        step === "verify_email" ? (
          <>
            <form className="auth-form auth-form-classic" onSubmit={handleEmailSignupVerify}>
              <input
                type="text"
                placeholder={t("auth.otp_code", "6-digit code")}
                value={otp}
                onChange={(event) => setOtp(event.target.value)}
                required
              />
              <button type="submit" disabled={loading}>
                {loading ? t("auth.loading", "Loading...") : t("auth.verify_code", "Verify code")}
              </button>
            </form>

            <button
              type="button"
              className="auth-text-link"
              onClick={handleResend}
              disabled={loading}
            >
              {t("auth.resend_code", "Resend code")}
            </button>

            <button type="button" className="auth-text-link" onClick={resetFlow}>
              {t("auth.back", "Back")}
            </button>
          </>
        ) : (
          <form className="auth-form auth-form-classic" onSubmit={handleEmailSignupStart}>
            <input
              type="email"
              placeholder={t("auth.email", "Email")}
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              required
            />
            <input
              type="password"
              placeholder={t("auth.password", "Password")}
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              required
            />
            <input
              type="password"
              placeholder={t("auth.confirm_password", "Confirm password")}
              value={confirmPassword}
              onChange={(event) => setConfirmPassword(event.target.value)}
              required
            />
            <button type="submit" disabled={loading}>
              {loading ? t("auth.loading", "Loading...") : t("auth.signup_btn", "Sign Up")}
            </button>
          </form>
        )
      ) : step === "wait_whatsapp" ? (
        renderWhatsappWaiting()
      ) : (
        <form className="auth-form auth-form-classic" onSubmit={startWhatsappChallenge}>
          <input
            type="tel"
            placeholder={t("auth.phone_placeholder", "Ex: +33612345678")}
            value={phone}
            onChange={(event) => setPhone(event.target.value)}
            required
          />
          <p className="auth-field-hint">
            {t(
              "auth.phone_helper",
              "Enter your number in international format, without the initial 0."
            )}
          </p>
          <input
            type="password"
            placeholder={t("auth.password", "Password")}
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            required
          />
          <input
            type="password"
            placeholder={t("auth.confirm_password", "Confirm password")}
            value={confirmPassword}
            onChange={(event) => setConfirmPassword(event.target.value)}
            required
          />
          <button type="submit" disabled={loading}>
            {loading ? t("auth.loading", "Loading...") : t("auth.signup_whatsapp_btn", "Create with WhatsApp")}
          </button>
        </form>
      )}

      <button
        type="button"
        className="auth-text-link"
        onClick={() => setModeWithDefaults("login")}
      >
        {t("auth.login_btn", "Log In")}
      </button>
    </>
  );

  return (
    <div className="auth-page auth-page-classic">
      <div className="auth-container auth-container-classic">
        <h2 className="auth-title auth-title-classic">
          {isAndroidApp || mode === "login"
            ? t("auth.login_title", "Log In")
            : t("auth.signup_title", "Create an account")}
        </h2>

        {isAndroidApp || mode === "login" ? renderLogin() : renderSignup()}

        {message && <p className="success-message">{message}</p>}
        {error && <p className="error-message">{error}</p>}
      </div>
    </div>
  );
};

export default AuthPage;
